import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout1 from "../../Layouts/Layout1";
import LayoutContainer from "../../Layouts/LayoutContainer";
import { ChevronLeft, HelpCircle, Pencil } from "lucide-react";
import RentalDataUseModal from "./RentalDataUseModal";
import CurrentAddressModal from "./CurrentAddressModal";
import { useRentalProfile } from "../../context/RentalProfileContext";
import { RENTAL_PROFILE_SECTIONS } from "../../Constants/rentalProfile";

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const formatMoveInLine = (address) => {
  const m = parseInt(address.moveInMonth, 10);
  const y = address.moveInYear || "";
  if (!Number.isFinite(m) || m < 1 || m > 12) {
    const yPart = y || "—";
    return `${yPart} - Today · Current address`;
  }
  const idx = m - 1;
  return `${MONTH_NAMES[idx]} ${y} - Today · Current address`;
};

const AddressHistory = () => {
  const navigate = useNavigate();
  const { profile, loading, saving, updateSection } = useRentalProfile();
  const [dataUseModalOpen, setDataUseModalOpen] = useState(false);
  const [currentAddress, setCurrentAddress] = useState(null);
  const [previousAddresses, setPreviousAddresses] = useState([]);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [addressModalEdit, setAddressModalEdit] = useState(false);

  useEffect(() => {
    if (loading) return;
    setCurrentAddress(profile.addressHistory.currentAddress || null);
    setPreviousAddresses(profile.addressHistory.previousAddresses || []);
  }, [loading, profile.addressHistory]);

  const openAddAddress = () => {
    setAddressModalEdit(false);
    setAddressModalOpen(true);
  };

  const openEditAddress = () => {
    setAddressModalEdit(true);
    setAddressModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await updateSection(RENTAL_PROFILE_SECTIONS.ADDRESS_HISTORY, {
      currentAddress,
      previousAddresses,
    });
    if (ok) navigate("/rental-applications");
  };

  const referenceLine =
    currentAddress?.addReference === "yes"
      ? "Reference provided"
      : "Address reference not provided";

  return (
    <Layout1>
      <LayoutContainer className="max-w-xl py-8 md:py-12 lg:py-14">
        <button
          type="button"
          onClick={() => navigate("/rental-applications")}
          className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-[#097FE6] hover:underline"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={2} />
          Back
        </button>

        <h1 className="text-2xl font-bold tracking-tight text-[#000929] md:text-3xl">
          Address history
        </h1>

        <div className="mt-8 rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-sm md:p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            <p className="text-sm leading-relaxed text-[#6B7280]">
              Add two or more years of your most recent address history and help
              verify your details with a valid reference. Your history could
              include living with parents or the property you own.
            </p>

            <div>
              <h2 className="text-base font-semibold text-[#000929]">
                Current address
              </h2>
              <p className="mt-1.5 text-sm leading-relaxed text-[#6B7280]">
                Please enter the residential address where you currently live.
              </p>

              {!currentAddress ? (
                <button
                  type="button"
                  onClick={openAddAddress}
                  className="mt-4 w-full rounded-lg border border-[#E5E7EB] bg-white py-3 text-sm font-semibold text-[#000929] transition-colors hover:bg-[#F9FAFB]"
                >
                  Add current address
                </button>
              ) : (
                <div className="mt-4 rounded-xl bg-[#F3F4F6] p-4 md:p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-[#000929]">
                        {currentAddress.street || "—"}
                      </p>
                      <p className="mt-1 text-sm text-[#6B7280]">
                        {[
                          currentAddress.city,
                          currentAddress.state,
                          currentAddress.postcode,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        {currentAddress.country
                          ? ` · ${currentAddress.country}`
                          : ""}
                      </p>
                      <p className="mt-2 text-sm text-[#6B7280]">
                        {formatMoveInLine(currentAddress)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={openEditAddress}
                      className="inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-[#097FE6] hover:underline"
                    >
                      <Pencil className="h-4 w-4" strokeWidth={2} />
                      Edit
                    </button>
                  </div>
                  <div className="my-4 border-t border-[#E5E7EB]" />
                  <p className="text-sm text-[#6B7280]">{referenceLine}</p>
                </div>
              )}
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading || saving}
                className="w-full rounded-lg bg-[#097FE6] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#0868c4] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save and back"}
              </button>
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => navigate("/rental-applications")}
                  className="text-sm font-semibold text-[#000929] hover:underline"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>

        <button
          type="button"
          onClick={() => setDataUseModalOpen(true)}
          className="mt-10 inline-flex items-center gap-2 text-sm text-[#6B7280] underline decoration-[#6B7280] underline-offset-2 hover:text-[#1A1A1A]"
        >
          <HelpCircle className="h-4 w-4 shrink-0" strokeWidth={2} />
          How this information is used
        </button>
      </LayoutContainer>

      <CurrentAddressModal
        open={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        existingAddress={addressModalEdit ? currentAddress : null}
        onSave={(data) => setCurrentAddress(data)}
        onDelete={() => setCurrentAddress(null)}
      />

      <RentalDataUseModal
        variant="addressHistory"
        open={dataUseModalOpen}
        onClose={() => setDataUseModalOpen(false)}
      />
    </Layout1>
  );
};

export default AddressHistory;
